import { matchPreset, normaliseGains, presetGains } from '../domain/eq';
import {
  applyPatch,
  createStation,
  findByUrl,
  markPlayed,
  missingSeedStations,
  moveStation,
  removeStation,
  reorderStation,
  restoreSeedStations,
  type StationInput,
  type StationPatch,
  toggleFavorite,
  upsertStation,
  visibleStations,
} from '../domain/library';
import {
  loadLibrary,
  requestPersistence,
  saveSettings,
  saveStations,
} from '../domain/library-repository';
import type { EqPreset, Settings, SortMode } from '../domain/settings';
import { capabilityIsFresh, type MetaCapability, type Station } from '../domain/stations';
import type { Lang } from '../i18n';
import type { StoragePort } from '../ports/storage';
import type { AppState } from './app';
import type { Store } from './index';

/**
 * Knjižnica kot storitev: čista logika iz `domain/library` + shramba + store.
 *
 * Vsaka metoda naredi troje v istem vrstnem redu — izračuna novo stanje,
 * ga objavi v store in ga shrani. Zapis v shrambo se **ne čaka**: uporabnik
 * ne sme čakati na `localStorage`, da se zvezdica obarva, shramba pa ne more
 * odpovedati na način, iz katerega bi se dalo okrevati (glej `web-storage`).
 */

export interface LibraryService {
  /** Prebere shrambo in objavi stanje. Do takrat je `libraryReady` `false`. */
  init(): Promise<void>;
  all(): readonly Station[];
  /** Filtriran in razvrščen seznam — vrstni red za ⏮ ⏭ (P2 §0.4). */
  visible(): readonly Station[];

  toggleFavorite(id: string): void;
  addStation(input: StationInput): Station | null;
  editStation(id: string, patch: StationPatch): void;
  deleteStation(id: string): void;
  move(id: string, step: 1 | -1): void;
  reorder(id: string, toIndex: number): void;
  /** Izbriše ves seed z enim dejanjem; ne vrne se sam od sebe (T2 §9). */
  clearSeed(): void;
  /** Koliko prednaloženih postaj v knjižnici manjka. */
  missingSeedCount(): number;
  /**
   * Doda manjkajoče prednaložene postaje. **Samo na uporabnikovo zahtevo.**
   *
   * Tiho prilivanje ob nadgradnji bi vračalo postaje, ki jih je namerno
   * izbrisal; izrecno dejanje tega problema nima.
   */
  restoreSeed(): number;

  setSortMode(mode: SortMode): void;
  setFavoritesOnly(on: boolean): void;
  setQuery(query: string): void;
  /**
   * Zapiše temo v nastavitve — samo za izvoz. Dejansko temo drži `data-theme`
   * prek `ds/js/theme.js` (T2 §2); to jo le zrcali v celotno kopijo.
   */
  setTheme(theme: 'dark' | 'light'): void;
  /** Zapiše jezik v nastavitve — za izvoz; efektivni jezik drži `store.lang`. */
  setLang(lang: Lang): void;

  /* ── Zvok (faza 6, P2 §6.3) ────────────────────────────────────── */

  /**
   * Glasnost 0–1 (P2 §3.5, faza 7).
   *
   * Shrani se, ker je pričakovanje uporabnika, da naslednji zagon igra enako
   * glasno. Tišina se hrani ločeno in ne kot `volume = 0`: vklop zvoka mora
   * vrniti prejšnjo glasnost, ne polne.
   */
  setVolume(value: number): void;
  setMuted(muted: boolean): void;

  setEqEnabled(on: boolean): void;
  /**
   * Prednastavitev prepiše **vse** ojačitve — to je njen ves pomen.
   * `custom` je izid uporabnikovega drsnika in ne izbira, zato krivulje nima.
   */
  setEqPreset(preset: EqPreset): void;
  /**
   * Ojačitve iz drsnikov. Prednastavitev se **izpelje**, ne shrani ločeno:
   * uporabnik lahko izbere »rock« in premakne en drsnik, in izbirnik mora
   * takrat pokazati »po meri«, sicer trdi nekaj, česar ni slišati.
   */
  setEqGains(gains: readonly number[]): void;
  setVisualizerEnabled(on: boolean): void;

  markPlayed(id: string): void;
  /** Trajno zapiše rezultat sonde (T2 §11, korak 4). */
  setResolved(id: string, url: string): void;
  /** Trajno zapiše izid kaskade metapodatkov (T2 §6). */
  setMetaCapability(id: string, capability: MetaCapability): void;

  replaceAll(stations: readonly Station[], settings?: Settings): void;
}

export interface LibraryDeps {
  storage: StoragePort;
  store: Store<AppState>;
  seed: readonly Station[];
  /** Za teste: determinističen `id` in ura. */
  newId?: () => string;
  now?: () => number;
}

function defaultId(): string {
  // `crypto.randomUUID` zahteva varen kontekst; ta je izpolnjen povsod, kjer
  // aplikacija teče (HTTPS ali localhost). Rezerva obstaja zato, da razvojno
  // okolje po HTTP na LAN ne pade.
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `st-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function createLibrary(deps: LibraryDeps): LibraryService {
  const { storage, store, seed } = deps;
  const newId = deps.newId ?? defaultId;
  const now = deps.now ?? (() => Date.now());

  /**
   * `persist()` se sme zahtevati **enkrat** in šele po uporabnikovi
   * investiciji — prvi priljubljeni ali prvi ročno dodani postaji (T2 §8).
   * Ob prvem izrisu bi bil poziv vsiljiv, ker uporabnik še nima česa izgubiti.
   */
  let persistenceAsked = false;
  function askPersistenceOnce(): void {
    if (persistenceAsked) return;
    persistenceAsked = true;
    void requestPersistence();
  }

  function commit(stations: readonly Station[]): void {
    store.set({ stations });
    void saveStations(storage, stations);
  }

  function patchSettings(patch: Partial<Settings>): void {
    const settings = { ...store.get().settings, ...patch };
    store.set({ settings });
    void saveSettings(storage, settings);
  }

  const service: LibraryService = {
    async init() {
      const snapshot = await loadLibrary(storage, seed);
      store.set({
        stations: snapshot.stations,
        settings: snapshot.settings,
        libraryReady: true,
        libraryFailure: snapshot.failure,
      });
    },

    all() {
      return store.get().stations;
    },

    visible() {
      const state = store.get();
      return visibleStations(state.stations, {
        sortMode: state.settings.sortMode,
        favoritesOnly: state.settings.favoritesOnly,
        query: state.query,
        lang: state.lang,
      });
    },

    toggleFavorite(id) {
      commit(toggleFavorite(store.get().stations, id));
      askPersistenceOnce();
    },

    addStation(input) {
      const stations = store.get().stations;
      // Podvajanje se ugotovi po naslovu (T2 §7, točka 5) — ime se rado
      // razlikuje za vejico, naslov pa je tisti, ki dejansko igra.
      if (findByUrl(stations, input.url) !== null) return null;

      const station = createStation(input, {
        id: newId(),
        now: now(),
        order: stations.length,
      });
      commit(upsertStation(stations, station));
      askPersistenceOnce();
      return station;
    },

    editStation(id, patch) {
      const stations = store.get().stations;
      const existing = stations.find((s) => s.id === id);
      if (existing === undefined) return;
      commit(upsertStation(stations, applyPatch(existing, patch)));
    },

    deleteStation(id) {
      commit(removeStation(store.get().stations, id));
    },

    move(id, step) {
      commit(moveStation(store.get().stations, id, step));
      // Premik je smiseln le v vrstnem redu po meri; sicer bi ga naslednji
      // izris takoj prekril z razvrstitvijo in premik bi izgledal, kot da ni deloval.
      patchSettings({ sortMode: 'custom' });
    },

    reorder(id, toIndex) {
      commit(reorderStation(store.get().stations, id, toIndex));
      patchSettings({ sortMode: 'custom' });
    },

    clearSeed() {
      const stations = store.get().stations.filter((s) => s.source !== 'seed');
      commit(stations);
      // Brez te zastavice bi se seed ob naslednjem zagonu vrnil: prazen
      // seznam je od »shrambe še ni bilo« nerazločljiv (T2 §9).
      patchSettings({ seedDismissed: true });
    },

    missingSeedCount() {
      return missingSeedStations(store.get().stations, seed).length;
    },

    restoreSeed() {
      const stations = store.get().stations;
      const added = missingSeedStations(stations, seed).length;
      if (added === 0) return 0;

      commit(restoreSeedStations(stations, seed));
      // Zastavica mora pasti, sicer bi seznam po tem dejanju veljal za
      // »seed zavrnjen« in naslednji prazen zagon ga ne bi naložil.
      patchSettings({ seedDismissed: false });
      return added;
    },

    setSortMode(mode) {
      patchSettings({ sortMode: mode });
    },

    setFavoritesOnly(on) {
      patchSettings({ favoritesOnly: on });
    },

    setQuery(query) {
      store.set({ query });
    },

    setTheme(theme) {
      patchSettings({ theme });
    },

    setLang(lang) {
      patchSettings({ lang });
    },

    setVolume(value) {
      // Meje postavi shramba in ne klicno mesto: bližnjica `↑` sešteva korake
      // in bi brez tega shranila 1,05.
      patchSettings({ volume: Math.min(1, Math.max(0, value)) });
    },

    setMuted(muted) {
      patchSettings({ muted });
    },

    setEqEnabled(on) {
      patchSettings({ eqEnabled: on });
    },

    setEqPreset(preset) {
      const gains = presetGains(preset, store.get().settings.eqGains);
      patchSettings({ eqPreset: preset, eqGains: gains });
    },

    setEqGains(gains) {
      const normalised = normaliseGains(gains);
      patchSettings({ eqGains: normalised, eqPreset: matchPreset(normalised) });
    },

    setVisualizerEnabled(on) {
      patchSettings({ visualizerEnabled: on });
    },

    markPlayed(id) {
      const stations = markPlayed(store.get().stations, id, now());
      store.set({ stations });
      void saveStations(storage, stations);
      patchSettings({ lastStationId: id });
    },

    setResolved(id, url) {
      const stations = store
        .get()
        .stations.map((station) =>
          station.id === id
            ? { ...station, urlResolved: url, isHttps: url.startsWith('https:') }
            : station,
        );
      commit(stations);
    },

    setMetaCapability(id, capability) {
      const stations = store.get().stations;
      const existing = stations.find((s) => s.id === id);
      if (existing === undefined) return;

      // Enak in še svež izid se ne zapisuje znova: vsak zapis je nov objekt in
      // nov izris seznama, kaskada pa se javi ob vsakem predvajanju. Ko zapis
      // postara, ga je treba osvežiti — sicer bi se kaskada od tam naprej
      // ponavljala ob vsakem predvajanju in žig se ne bi nikoli premaknil.
      if (
        capabilityIsFresh(existing, now()) &&
        JSON.stringify(existing.metaCapability) === JSON.stringify(capability)
      ) {
        return;
      }

      commit(
        stations.map((station) =>
          station.id === id
            ? { ...station, metaCapability: capability, metaCheckedAt: now() }
            : station,
        ),
      );
    },

    replaceAll(stations, settings) {
      commit(stations);
      if (settings !== undefined) patchSettings(settings);
    },
  };

  return service;
}
